import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, User, DollarSign, X } from "lucide-react";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { IndividualsRepository } from "@/repositories/individuals.repository";
import { mayGiveWorker, typedPay } from "@/lib/appointment-assignment";
import { typedMoneyProblem } from "@/lib/typed-money";

interface AssignableIndividual {
  id: string;
  name: string;
}

export type WorkerAssignmentRequest = { individualId: string; pay: number | undefined };

interface SimpleAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: AppointmentJoin;
  onAssign: (assignment: WorkerAssignmentRequest) => Promise<void>;
  isLoading?: boolean;
}

export default function SimpleAssignmentModal({
  isOpen,
  onClose,
  appointment,
  onAssign,
  isLoading = false,
}: SimpleAssignmentModalProps) {
  const currentWorker = appointment.assigned_user?.id ?? "";
  const currentPay = appointment.individual_payment ?? null;
  const [selectedIndividual, setSelectedIndividual] = useState<string>(currentWorker);
  const [payText, setPayText] = useState("");
  const [individuals, setIndividuals] = useState<AssignableIndividual[]>([]);
  const [loadingIndividuals, setLoadingIndividuals] = useState(false);
  const onlyCurrentWorker = !mayGiveWorker(appointment.status, currentWorker !== "");
  const keepsCurrentWorker = currentWorker !== "" && selectedIndividual === currentWorker;

  useEffect(() => {
    if (isOpen) {
      loadIndividuals();
      setSelectedIndividual(currentWorker);
    } else {
      setSelectedIndividual("");
    }
  }, [isOpen]);

  useEffect(() => {
    setPayText(keepsCurrentWorker && currentPay !== null ? String(currentPay) : "");
  }, [selectedIndividual]);

  const loadIndividuals = async () => {
    setLoadingIndividuals(true);
    try {
      const assignableIndividuals = await IndividualsRepository.listForAssignment();
      setIndividuals(onlyCurrentWorker ? assignableIndividuals.filter((individual) => individual.id === currentWorker) : assignableIndividuals);
    } catch (error) {
      console.error("Failed to load individuals:", error);
      setIndividuals([]);
    } finally {
      setLoadingIndividuals(false);
    }
  };

  const handleClose = () => {
    setSelectedIndividual("");
    setPayText("");
    onClose();
  };

  const pay = typedPay(payText);
  const payProblem = typedMoneyProblem(payText);

  const handleAssign = async () => {
    if (!selectedIndividual || pay === null) {
      return;
    }

    try {
      await onAssign({ individualId: selectedIndividual, pay });
      handleClose();
    } catch (error) {
      console.error("Assignment failed:", error);
    }
  };

  const isFormValid = Boolean(selectedIndividual) && pay !== null;

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4">
        {/* Close button */}
        <button onClick={handleClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold">Assign Individual</h2>
          <p className="text-sm text-gray-600">
            Assign an individual from your organization to this appointment and set the payment amount.
          </p>
        </div>

        {/* Form */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="individual">Assign Individual</Label>
            {loadingIndividuals ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="ml-2">Loading individuals...</span>
              </div>
            ) : (
              <Select value={selectedIndividual} onValueChange={setSelectedIndividual}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select individual" />
                </SelectTrigger>
                <SelectContent>
                  {individuals.length > 0 ? (
                    individuals.map((individual) => (
                      <SelectItem key={individual.id} value={individual.id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {individual.name}
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">No individuals available</div>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment">Payment Amount</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                id="payment"
                type="number"
                min="0"
                step="0.01"
                value={payText}
                onChange={(e) => setPayText(e.target.value)}
                className="pl-10"
                placeholder="Enter payment amount"
              />
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {keepsCurrentWorker ? "Leave blank to keep the current pay" : "Leave blank to pay nothing"}
            </p>
            {payProblem && <p className="text-sm text-destructive">{payProblem}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!isFormValid || isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Please wait
              </>
            ) : (
              "Assign Individual"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
